import { useState } from 'react';
import { assertNever, type FinanceContactDto } from '@signalvote/shared';
import { useFinanceContacts, useMergeContacts } from '@/hooks/queries';
import { Button } from '@/components/ui/button';
import { formatAmount } from '../utils';
import { CLIENT_CONFLICT_MARKER } from '@/lib/clientConflict';

/**
 * Merge two records for the same person, from the contact page.
 *
 * <p>The thing this screen is really guarding against is not a wrong click — it is a treasurer who
 * is unsure what a merge does and abandons it, leaving a donor's giving split across two records
 * and their contribution limit measured against the wrong total. So every step says, in plain
 * words, what is about to happen and what becomes of each record.</p>
 *
 * <p>The record you are already looking at is pre-selected to survive. That is a SUGGESTION, not a
 * decision: it is predictable (you opened it on purpose) where a scoring heuristic would not be,
 * and it is one click to switch. The server deliberately does not force a winner either — a
 * treasurer may have a reason we cannot see, and overriding them silently is worse than asking.</p>
 */
interface MergeContactPanelProps {
  /** The record whose page this is — pre-selected as the one to keep. */
  contact: FinanceContactDto;
  onClose: () => void;
  /** Fired once the merge lands, with the id of the record that survived. */
  onMerged: (survivorContactId: string) => void;
}

/** "3 donations" / "1 donation" — the figure a treasurer checks before committing. */
function giftCount(contact: FinanceContactDto): string {
  const n = contact.contributionCount ?? 0;
  return `${n} ${n === 1 ? 'donation' : 'donations'}`;
}

export function MergeContactPanel({ contact, onClose, onMerged }: MergeContactPanelProps) {
  // Hold the picked record itself, not its id: the candidate list is a server-side search result,
  // so a later keystroke can page the chosen contact out of view without un-choosing it.
  const [other, setOther] = useState<FinanceContactDto | null>(null);
  // Set when the server merged into a DIFFERENT record than the treasurer chose (see submit).
  // Carries the surviving id too, so the acknowledgement can send them somewhere that still exists.
  const [overrodeChoice, setOverrodeChoice] = useState<
    { keptName: string; survivorContactId: string } | null
  >(null);
  const [survivorId, setSurvivorId] = useState(contact.id);
  const [picking, setPicking] = useState(false);
  const [search, setSearch] = useState('');
  const [amendmentNames, setAmendmentNames] = useState<string[] | null>(null);
  const [blockReason, setBlockReason] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Search server-side. Filtering a first page in the browser would make every contact past the
  // page size unreachable — and the duplicate a treasurer comes here to fix is exactly the one the
  // automatic check never flagged, so this picker is its only route. 100 is the server's clamp.
  const { data } = useFinanceContacts({ search: search.trim(), page: 1, pageSize: 100 });
  const { mutateAsync: mergeAsync, isPending } = useMergeContacts();

  const candidates = (data?.items ?? []).filter((c) => c.id !== contact.id);

  const survivor = survivorId === contact.id ? contact : other;
  const loser = survivorId === contact.id ? other : contact;

  async function submit(confirmAmendment: boolean) {
    if (!survivor || !loser) return;
    setErrorMessage(null);
    setBlockReason(null);
    try {
      const result = await mergeAsync({
        survivorContactId: survivor.id,
        loserContactId: loser.id,
        survivorConcurrencyVersion: survivor.concurrencyVersion,
        loserConcurrencyVersion: loser.concurrencyVersion,
        confirmAmendment,
      });
      switch (result.outcome) {
        case 'NeedsAmendmentConfirmation':
          setAmendmentNames(result.affectedFiledReportNames);
          return;
        case 'Blocked':
          setBlockReason(result.blockReason ?? 'This merge cannot be completed.');
          return;
        case 'Merged':
          // The server keeps a DESIGNATED record (the candidate contact, a processor's fee payee)
          // whichever way the treasurer chose, swapping survivor and loser to do it. That is a rule
          // we cannot talk it out of — but the summary above promised the opposite, so saying
          // nothing would leave them believing a record survived that no longer exists. Tell them
          // what actually happened instead of closing as if it went to plan.
          if (result.survivorContactId !== survivor.id) {
            setOverrodeChoice({
              keptName: loser.name,
              survivorContactId: result.survivorContactId,
            });
            return;
          }
          onMerged(result.survivorContactId);
          return;
        default:
          assertNever(result.outcome);
      }
    } catch (err) {
      setErrorMessage(
        err instanceof Error && err.message === CLIENT_CONFLICT_MARKER
          ? 'One of these records changed while you were here. Reopen and try again.'
          : 'That merge could not be completed. Nothing was changed.',
      );
    }
  }

  return (
    <div data-testid="merge-contact-panel" className="space-y-4 rounded-md border p-4">
      <div>
        <h3 className="text-base font-semibold">Merge with another record</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          If this person has two records, merging combines them into one. Every donation moves to
          the record you keep. Where the record you keep has a field filled in, its value stays;
          where it is blank, the other record fills it in.
        </p>
      </div>

      {/* Step 1 — which record is the same person */}
      <div className="space-y-2">
        <p className="text-sm font-medium">Which record is the same person?</p>
        <Button
          type="button"
          variant="outline"
          data-testid="merge-pick-other"
          onClick={() => setPicking((open) => !open)}
        >
          {other ? other.name : 'Choose the other record'}
        </Button>

        {picking && (
          <div className="space-y-2 rounded-md border p-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name…"
              aria-label="Search contacts"
              data-testid="merge-search"
              className="w-full rounded-md border px-2 py-1 text-sm"
            />
            {/* Without this, "still loading", "no match" and "no other contacts" all render as the
                same blank box — and a treasurer reads a blank box as "this is broken". */}
            {candidates.length === 0 && (
              <p data-testid="merge-no-candidates" className="px-2 py-1 text-sm text-muted-foreground">
                {data ? 'No other contacts match that name.' : 'Loading contacts…'}
              </p>
            )}
            <ul className="max-h-48 space-y-1 overflow-auto">
              {candidates.map((candidate) => (
                <li key={candidate.id}>
                  <button
                    type="button"
                    className="w-full rounded px-2 py-1 text-left text-sm hover:bg-accent"
                    onClick={() => {
                      setOther(candidate);
                      // Re-seat the choice on the record you opened. Without this, switching the
                      // survivor and THEN changing who the duplicate is leaves survivorId pointing
                      // at a record no longer on screen: no radio reads as checked, yet the button
                      // still names it and the request still sends it.
                      setSurvivorId(contact.id);
                      setPicking(false);
                    }}
                  >
                    {candidate.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Step 2 — which one survives. Pre-selected, never decided for them. */}
      {other && (
        <div className="space-y-2">
          <p className="text-sm font-medium">Which record should we keep?</p>
          {[contact, other].map((option) => (
            <label
              key={option.id}
              className="flex cursor-pointer items-start gap-2 rounded-md border p-2 text-sm"
            >
              <input
                type="radio"
                name="merge-survivor"
                data-testid={`merge-keep-${option.id}`}
                checked={survivorId === option.id}
                onChange={() => setSurvivorId(option.id)}
                className="mt-1"
              />
              <span>
                <span className="font-medium">{option.name}</span>
                {option.id === contact.id && (
                  <span className="ml-2 text-xs text-muted-foreground">(the one you opened)</span>
                )}
                <span className="block text-xs text-muted-foreground">
                  {giftCount(option)} · {formatAmount(option.calendarYearTotal)} this year
                  {option.email ? ` · ${option.email}` : ''}
                </span>
              </span>
            </label>
          ))}
        </div>
      )}

      {/* Step 3 — say exactly what is about to happen, in their words, before they commit. */}
      {survivor && loser && (
        <p data-testid="merge-summary" className="rounded-md bg-muted/40 p-3 text-sm">
          <strong>{loser.name}</strong> will be merged into <strong>{survivor.name}</strong>. Its{' '}
          {giftCount(loser)} will move across, and the totals for{' '}
          <strong>{survivor.name}</strong> will be recalculated. The record for{' '}
          <strong>{loser.name}</strong> is then removed. This cannot be undone.
        </p>
      )}

      {amendmentNames && (
        <div
          data-testid="merge-amendment-notice"
          className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm"
        >
          <p>
            One of these records is already named on a report you have filed:{' '}
            <strong>{amendmentNames.join(', ')}</strong>. Merging changes the name that report
            shows, so you will need to amend it.
          </p>
          <Button
            type="button"
            size="sm"
            data-testid="merge-confirm-amendment"
            disabled={isPending}
            onClick={() => submit(true)}
          >
            I understand — merge anyway
          </Button>
        </div>
      )}

      {overrodeChoice && (
        <div
          data-testid="merge-overrode-choice"
          className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm"
        >
          <p>
            The records were combined, but <strong>{overrodeChoice.keptName}</strong> is the one
            that was kept — it is designated as the candidate or a processor&apos;s fee payee, which
            has to survive. Everything moved onto it.
          </p>
          {/* onMerged, not onClose: on a swap the page behind this panel IS the record that was
              just deleted, so dismissing onto it would strand the treasurer on a 404 — the very
              confusion this branch exists to prevent. */}
          <Button
            type="button"
            size="sm"
            data-testid="merge-overrode-ack"
            onClick={() => onMerged(overrodeChoice.survivorContactId)}
          >
            Got it
          </Button>
        </div>
      )}

      {blockReason && (
        <p
          data-testid="merge-blocked"
          className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm"
        >
          {blockReason}
        </p>
      )}

      {errorMessage && (
        <p data-testid="merge-error" className="text-sm text-destructive">
          {errorMessage}
        </p>
      )}

      <div className="flex gap-2">
        <Button
          type="button"
          data-testid="merge-confirm"
          disabled={!other || isPending || !!amendmentNames || !!overrodeChoice}
          onClick={() => submit(false)}
        >
          {survivor && other ? `Merge into ${survivor.name}` : 'Merge'}
        </Button>
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
